import {Box, Center, Spinner, Text, VStack} from '@chakra-ui/react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHalState, useHalStates} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {ProfilUBDesc} from 'api-types/ffconnect-hal';
import {usePanier, useSearchClient} from 'ff-common';
import {HEADER_HEIGHT} from '@/constants/layout';
import {
  MesProduitsUBFiltersBar,
  UBStatusFilter,
} from './MesProduitsUBFiltersBar';
import {MesProduitsUBTags} from './MesProduitsUBTags';
import {MesProduitsUBSectionItem} from './MesProduitsUBSectionItem';

/**
 * Détail d'un profil UB dans "Mes Produits".
 * Affiche les filtres par statut, la barre de tags d'items et les sections
 * accordéon avec les articles correspondants.
 */
export const MesProduitsUBProfilDetail: React.FC<{
  profilUB: State<ProfilUBDesc>;
  showHeader?: boolean;
}> = ({profilUB, showHeader = false}) => {
  const {t} = useTranslation();
  const [filter, setFilter] = useState<UBStatusFilter>('tout');
  const [filtersHeaderHeight, setFiltersHeaderHeight] = useState(0);
  const filtersHeaderRef = useRef<HTMLDivElement>(null);
  const itemsHook = useHalState(profilUB.follow('items'));
  const items = itemsHook.data?.items;

  const lignesDePanier =
    useHalStates(usePanier().followAll('lignes_de_panier')).states ?? [];

  // Ensembles présents au catalogue FF : permet à chaque section de masquer le
  // lien "Voir tous les produits de l'UB" quand l'UB n'a aucun produit.
  // `undefined` tant que l'index de recherche n'est pas construit.
  const {
    indexIsBuilt,
    indexVersion,
    categoriesStdAvecProduits,
    motsDirecteursAvecProduits: collecterMotsDirecteursAvecProduits,
  } = useSearchClient();
  const categoriesAvecProduits = useMemo(
    () => (indexIsBuilt ? categoriesStdAvecProduits() : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indexIsBuilt, indexVersion],
  );
  const motsDirecteursAvecProduits = useMemo(
    () => (indexIsBuilt ? collecterMotsDirecteursAvecProduits() : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indexIsBuilt, indexVersion],
  );

  const filteredItems = useMemo(() => {
    if (!items) return undefined;
    if (filter === 'tout') return items;
    if (filter === 'actives')
      return items.filter((i) => i.statut === 'gagne' || i.statut === 'ras');
    return items.filter((i) => i.statut === filter);
  }, [items, filter]);

  // Hauteur du bloc sticky (filtres + tags) pour caler les headers de section dessous.
  useEffect(() => {
    const el = filtersHeaderRef.current;
    if (!el) return;

    const updateHeight = () => {
      setFiltersHeaderHeight(el.clientHeight);
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);
    return () => {
      resizeObserver.disconnect();
    };
  }, [filteredItems?.length]);

  // Sur md+, les filtres sont sticky : les headers de section se calent en dessous.
  // Sur base, les filtres défilent : headers inchangés sous la Navbar.
  // Même valeur en `scrollMarginTop` des sections, pour le clic sur un tag.
  const sectionStickyTop = useMemo(
    () => ({
      base: HEADER_HEIGHT.base,
      md:
        filtersHeaderHeight > 0
          ? `calc(${HEADER_HEIGHT.md} + ${filtersHeaderHeight}px)`
          : HEADER_HEIGHT.md,
    }),
    [filtersHeaderHeight],
  );

  if (itemsHook.loading) {
    return (
      <Center py='10'>
        <Spinner />
      </Center>
    );
  }

  return (
    <VStack
      alignItems='stretch'
      spacing={3}
      py={{base: '2', md: '4'}}
      px={{base: '2', md: '4', lg: '6'}}
    >
      {showHeader && (
        <Text
          fontWeight='700'
          fontSize='lg'
          color='gray.dark'
        >
          {profilUB.data.nom}
        </Text>
      )}
      {/* Sticky md+ : filtres tendance + tags UB (même pattern que le Cadencier) */}
      <Box
        ref={filtersHeaderRef}
        width='100%'
        zIndex='dropdown'
        position={{md: 'sticky'}}
        top={{md: HEADER_HEIGHT.md}}
        bg='white'
        py='3'
      >
        {/* Barre de filtres par statut "Tout / Actives / Perdu / En baisse / À gagner" */}
        <MesProduitsUBFiltersBar
          items={items}
          selected={filter}
          onChange={setFilter}
        />
        {/* Tags UB */}
        {filteredItems && filteredItems.length > 0 && (
          <MesProduitsUBTags items={filteredItems} />
        )}
      </Box>

      {/* Sections UB */}
      <Box
        bg='white'
        border='1px solid'
        borderColor='gray.light'
        borderRadius='12px'
        boxShadow='md'
      >
        {filteredItems && filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <MesProduitsUBSectionItem
              key={item.id}
              item={item}
              lignesDePanier={lignesDePanier}
              categoriesAvecProduits={categoriesAvecProduits}
              motsDirecteursAvecProduits={motsDirecteursAvecProduits}
              stickyTop={sectionStickyTop}
            />
          ))
        ) : (
          <Box p='6'>
            <Text
              textAlign='center'
              color='gray.dark'
            >
              {t('client.mes_produits.ub_essentielles.aucun_item_filtre')}
            </Text>
          </Box>
        )}
      </Box>
    </VStack>
  );
};
